import { useEffect } from 'react';

const ensureMetaTag = (selector, attributes) => {
  if (typeof document === 'undefined') return null;

  let element = document.head.querySelector(selector);
  if (element) return element;

  element = document.createElement('meta');
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
  document.head.appendChild(element);
  return element;
};

const getMetaElement = (selector, fallbackAttributes) =>
  ensureMetaTag(selector, fallbackAttributes);

const readContent = (element) => element?.getAttribute('content') || '';

const writeContent = (element, value) => {
  if (!element) return;
  element.setAttribute('content', value);
};

const useMetaTags = (title, description, url) => {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!title && !description && !url) return undefined;

    const ogTitle = getMetaElement('meta[property="og:title"]', { property: 'og:title' });
    const ogDescription = getMetaElement('meta[property="og:description"]', { property: 'og:description' });
    const ogUrl = getMetaElement('meta[property="og:url"]', { property: 'og:url' });
    const twitterTitle =
      document.head.querySelector('meta[property="twitter:title"]') ||
      getMetaElement('meta[name="twitter:title"]', { name: 'twitter:title' });
    const twitterDescription =
      document.head.querySelector('meta[property="twitter:description"]') ||
      getMetaElement('meta[name="twitter:description"]', { name: 'twitter:description' });
    const metaDescription = getMetaElement('meta[name="description"]', { name: 'description' });
    const metaTitle = getMetaElement('meta[name="title"]', { name: 'title' });

    const originalTitle = document.title;
    const originalOgTitle = readContent(ogTitle);
    const originalOgDescription = readContent(ogDescription);
    const originalOgUrl = readContent(ogUrl);
    const originalTwitterTitle = readContent(twitterTitle);
    const originalTwitterDescription = readContent(twitterDescription);
    const originalMetaDescription = readContent(metaDescription);
    const originalMetaTitle = readContent(metaTitle);

    if (title) {
      document.title = title;
      writeContent(ogTitle, title);
      writeContent(twitterTitle, title);
      writeContent(metaTitle, title);
    }

    if (description) {
      writeContent(ogDescription, description);
      writeContent(twitterDescription, description);
      writeContent(metaDescription, description);
    }

    if (url) {
      writeContent(ogUrl, url);
    }

    return () => {
      document.title = originalTitle;
      writeContent(ogTitle, originalOgTitle);
      writeContent(ogDescription, originalOgDescription);
      writeContent(ogUrl, originalOgUrl);
      writeContent(twitterTitle, originalTwitterTitle);
      writeContent(twitterDescription, originalTwitterDescription);
      writeContent(metaDescription, originalMetaDescription);
      writeContent(metaTitle, originalMetaTitle);
    };
  }, [description, title, url]);
};

export default useMetaTags;
